import { useEffect, useRef, useState } from "react";
import CaseWorkspace, {
  CaseFacts,
  CaseNavigation,
} from "@/components/case-workspace";
import {
  forgetProposalDraft,
  rememberProposalDraft,
  restoreProposalDraft,
  proposalDraftKey,
} from "./proposal-draft";
import { PageCanvas, FormContent, FormSection } from "@/components/page-canvas";
import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FormErrors } from "@/components/forms/form-errors";
import {
  ServiceEditor,
  DiscountField,
  TotalsSummary,
  linePayload,
  changedServices,
  serviceCurrency,
  type EditorLine,
} from "@/components/commercial/service-editor";
import {
  locationRequestPath,
  locationRequestProposalDraftPath,
  editLocationRequestProposalDraftPath,
} from "@/routes";
import { Choice } from "../requests/controls";
type Offering = {
  id: string;
  revision: string;
  service_revision: string;
  name: string;
  price: string;
  currency: string;
  tax_rate: string;
  tax_label: string;
  tax_mode: string;
};
type InputValues = {
  assignee_id?: string;
  helper_ids?: string[];
  bay_id?: string;
  starts_at?: string;
  ends_at?: string;
  starts_at_offset?: string;
  ends_at_offset?: string;
  discount_amount?: string;
  services?: Partial<EditorLine>[];
};
type ProposalFormProps = {
  enquiry: {
    id: string;
    revision: string;
    name: string;
    vehicle: string;
    state: string;
  };
  section: "services" | "schedule";
  sent_id: string | null;
  prerequisites: {
    can_manage: boolean;
    administrators: string[];
    services_path: string | null;
    bays_path: string | null;
  };
  draft_revision: string | null;
  input: InputValues;
  offerings: Offering[];
  staff: { id: string; name: string }[];
  bays: { id: string; name: string }[];
  time_choices: Partial<Record<"starts_at" | "ends_at", string[]>>;
};
export default function ProposalForm(props: ProposalFormProps) {
  const { auth } = usePage().props;
  const draftKey = proposalDraftKey({
    account: auth.account?.id || "",
    actor: auth.user?.id || "",
    location: auth.location?.id || "",
    request: props.enquiry.id,
  });
  return <ProposalEditor key={draftKey} {...props} draftKey={draftKey} />;
}
function ProposalEditor({
  enquiry,
  section,
  sent_id,
  prerequisites,
  draft_revision,
  input,
  offerings,
  bays,
  time_choices,
  staff,
  draftKey,
}: ProposalFormProps & { draftKey: string }) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  const options: EditorLine[] = offerings.map((offering) => ({
    id: offering.id,
    offering_id: offering.id,
    offering_revision: offering.revision,
    service_revision: offering.service_revision,
    name: offering.name,
    quantity: "1",
    unit_price: offering.price,
    discount_amount: "0",
    discount_percentage: "0",
    tax_mode: offering.tax_mode,
    tax_rate: offering.tax_rate,
    currency: offering.currency,
  }));
  const services = (input.services || []).flatMap((line) => {
    const option = options.find(
      (value) => value.offering_id === line.offering_id,
    );
    return option
      ? [
          {
            ...option,
            ...line,
            quantity: String(line.quantity ?? 1),
            unit_price: String(line.unit_price ?? option.unit_price),
            discount_amount: String(line.discount_amount ?? "0"),
            discount_percentage: String(line.discount_percentage ?? "0"),
          },
        ]
      : [
          {
            id: line.offering_id || "missing",
            name: t("workflow.unavailable_service"),
            quantity: String(line.quantity ?? 1),
            unit_price: String(line.unit_price ?? "0"),
            discount_amount: String(line.discount_amount ?? "0"),
            discount_percentage: String(line.discount_percentage ?? "0"),
            ...line,
          } as EditorLine,
        ];
  });
  const saved = useRef(false);
  const [recovered, setRecovered] = useState(false);
  const [recoveryUnavailable, setRecoveryUnavailable] = useState(false);
  const form = useForm({
    request_revision: enquiry.revision,
    revision: draft_revision || "",
    proposal: {
      assignee_id: input.assignee_id || "",
      helper_ids: input.helper_ids || [],
      bay_id: input.bay_id || "",
      starts_at: input.starts_at || "",
      ends_at: input.ends_at || "",
      starts_at_offset: input.starts_at_offset || "",
      ends_at_offset: input.ends_at_offset || "",
      discount_amount: String(input.discount_amount || "0"),
      services,
    },
  });
  const initial = useRef(form.data);
  const setData = form.setData;
  useEffect(() => {
    setData(restoreProposalDraft(draftKey, initial.current));
    setRecovered(true);
  }, [draftKey, setData]);
  useEffect(() => {
    if (recovered && !saved.current)
      setRecoveryUnavailable(!rememberProposalDraft(draftKey, form.data));
  }, [draftKey, form.data, recovered]);
  if (!auth.location) return null;
  const location = auth.location.id,
    values = form.data.proposal;
  const set = (patch: Partial<typeof values>) =>
    form.setData("proposal", { ...values, ...patch });
  const stale =
    form.data.request_revision !== enquiry.revision ||
    form.data.revision !== (draft_revision || "");
  const setupHelp = (kind: "services" | "bays") => {
    const path =
      kind === "services"
        ? prerequisites.services_path
        : prerequisites.bays_path;
    return (
      <section
        role="status"
        className="space-y-3 rounded-lg border bg-muted/40 p-4"
      >
        <h2 className="font-semibold">
          {t(`product_ux_proposal.setup_${kind}`)}
        </h2>
        <p className="text-sm text-muted-foreground">
          {!recoveryUnavailable && t("product_ux_proposal.setup_help")}
        </p>
        {prerequisites.can_manage && path ? (
          recoveryUnavailable ? (
            <a
              className={buttonVariants({ variant: "outline" })}
              href={path}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("product_ux_proposal.setup_new_tab")}
            </a>
          ) : (
            <Link
              className={buttonVariants({ variant: "outline" })}
              href={path}
              onClick={(event) => {
                if (!rememberProposalDraft(draftKey, form.data)) {
                  event.preventDefault();
                  setRecoveryUnavailable(true);
                }
              }}
            >
              {t(`product_ux_proposal.manage_${kind}`)}
            </Link>
          )
        ) : (
          <>
            <p className="text-sm">
              {t("product_ux_proposal.setup_handoff", {
                location: auth.location?.name,
              })}
            </p>
            {prerequisites.administrators.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {prerequisites.administrators.join(", ")}
              </p>
            )}
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            router.reload({
              only: ["offerings", "bays", "staff", "prerequisites"],
            })
          }
        >
          {t("product_ux_proposal.refresh_setup")}
        </Button>
      </section>
    );
  };
  return (
    <AppLayout
      showWorkspaceNavigation={false}
      breadcrumbs={[
        {
          title: t("requests.title"),
          href: locationRequestPath(location, enquiry.id),
        },
      ]}
    >
      <Head title={t("proposals.compose")} />
      <PageCanvas>
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                {
                  label: t("product_ux_proposal.customer"),
                  value: enquiry.name,
                },
                {
                  label: t("product_ux_proposal.vehicle"),
                  value: enquiry.vehicle,
                },
                {
                  label: t("product_ux_proposal.status"),
                  value: t("product_ux_proposal.private_draft"),
                },
              ]}
            />
          }
          navigation={
            <CaseNavigation
              preserveState
              label={t("product_ux_proposal.sections")}
              active={section}
              items={[
                {
                  id: "services",
                  label: t("product_ux_proposal.services"),
                  href: editLocationRequestProposalDraftPath(
                    location,
                    enquiry.id,
                    { section: "services" },
                  ),
                },
                {
                  id: "schedule",
                  label: t("product_ux_proposal.schedule"),
                  href: editLocationRequestProposalDraftPath(
                    location,
                    enquiry.id,
                    { section: "schedule" },
                  ),
                },
                {
                  id: "review",
                  label: t("product_ux_proposal.review_saved"),
                  href: locationRequestProposalDraftPath(location, enquiry.id, {
                    section: "review",
                  }),
                  disabled: !draft_revision,
                },
                {
                  id: "delivery",
                  label: t("product_ux_proposal.delivery"),
                  href: locationRequestProposalDraftPath(location, enquiry.id, {
                    section: "delivery",
                  }),
                  disabled: !sent_id,
                },
              ]}
            />
          }
        >
          <FormContent>
            <div>
              <h1 className="text-page font-display">
                {t("proposals.compose")}
              </h1>
              <p className="mt-2">{enquiry.name}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("proposals.private_help")}
              </p>
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (section === "services") {
                  router.visit(
                    editLocationRequestProposalDraftPath(location, enquiry.id, {
                      section: "schedule",
                    }),
                    { preserveState: true },
                  );
                  return;
                }
                form.transform((data) => ({
                  ...data,
                  section,
                  proposal: {
                    ...data.proposal,
                    services: data.proposal.services.map(linePayload),
                  },
                }));
                form.post(
                  locationRequestProposalDraftPath(location, enquiry.id),
                  {
                    onSuccess: () => {
                      saved.current = true;
                      forgetProposalDraft(draftKey);
                    },
                  },
                );
              }}
            >
              {recoveryUnavailable && (
                <p role="alert" className="mb-4 rounded-lg border p-4 text-sm">
                  {t("product_ux_proposal.recovery_unavailable")}
                </p>
              )}
              <FormErrors
                errors={form.errors}
                targets={{
                  bay: "proposal-bay",
                  starts_at: "proposal-starts_at",
                  ends_at: "proposal-ends_at",
                  assignee: "proposal-assignee",
                  quantity:
                    values.services.length === 1
                      ? "proposal-quantity-0"
                      : "proposal-services",
                  services: "proposal-services",
                }}
              />
              {stale && (
                <section className="space-y-3 rounded-lg border p-4">
                  <p role="alert">{t("product_ux_proposal.stale")}</p>
                  <p>{enquiry.name}</p>
                  <details>
                    <summary className="cursor-pointer py-2">
                      {t("workflow.current_terms")}
                    </summary>
                    <p>
                      {input.starts_at} – {input.ends_at}
                    </p>
                    {input.services?.map((line, index) => (
                      <p key={index}>
                        {options.find(
                          (option) => option.offering_id === line.offering_id,
                        )?.name || t("jobs.services")}{" "}
                        · {line.quantity || 1} × {line.unit_price}
                      </p>
                    ))}
                  </details>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      form.setData({
                        ...form.data,
                        request_revision: enquiry.revision,
                        revision: draft_revision || "",
                      });
                      form.clearErrors();
                    }}
                  >
                    {t("workflow.review_context")}
                  </Button>
                </section>
              )}
              <div className="mt-5 space-y-6">
                <div className="min-w-0 space-y-6">
                  {section === "services" && (
                    <>
                      {offerings.length === 0 && setupHelp("services")}
                      <ServiceEditor
                        errors={form.errors}
                        idPrefix="proposal"
                        lines={values.services}
                        options={options}
                        onChange={(services) => set({ services })}
                      />
                      <FormSection>
                        <DiscountField
                          percentageEnabled={false}
                          id="proposal-discount"
                          amount={values.discount_amount}
                          percentage="0"
                          onChange={(amount) =>
                            set({ discount_amount: amount })
                          }
                        />
                      </FormSection>
                      <TotalsSummary
                        lines={values.services}
                        amount={values.discount_amount}
                        percentage="0"
                      />
                    </>
                  )}
                  {section === "schedule" && (
                    <>
                      {bays.length === 0 && setupHelp("bays")}
                      <fieldset className="space-y-4 rounded-lg border p-4">
                        <legend className="px-1 font-semibold">
                          {t("jobs.interval")}
                        </legend>
                        <p className="text-sm text-muted-foreground">
                          {t("jobs.zone_help", { zone: i18n.time_zone })}
                        </p>
                        <Choice
                          id="proposal-bay"
                          name="proposal[bay_id]"
                          label={t("jobs.bay_id")}
                          options={bays}
                          value={values.bay_id}
                          onValueChange={(value) =>
                            set({ bay_id: value || "" })
                          }
                        />
                        <div className="form-fields">
                          {(["starts_at", "ends_at"] as const).map((field) => (
                            <div key={field} className="space-y-2">
                              <Label htmlFor={`proposal-${field}`}>
                                {t(`jobs.${field}`)}
                              </Label>
                              <Input
                                id={`proposal-${field}`}
                                type="datetime-local"
                                required
                                value={values[field]}
                                onChange={(event) =>
                                  set({ [field]: event.target.value })
                                }
                                aria-invalid={
                                  !!(form.errors as Record<string, unknown>)[
                                    field
                                  ]
                                }
                              />
                              {(time_choices[field]?.length || 0) > 1 && (
                                <Choice
                                  id={`proposal-${field}-offset`}
                                  name={`proposal[${field}_offset]`}
                                  label={t("jobs.occurrence")}
                                  defaultValue={values[`${field}_offset`]}
                                  onValueChange={(value) =>
                                    set({ [`${field}_offset`]: value || "" })
                                  }
                                  options={time_choices[field]!.map(
                                    (offset, index) => ({
                                      id: offset,
                                      name: t(
                                        index
                                          ? "jobs.second_occurrence"
                                          : "jobs.first_occurrence",
                                        { offset },
                                      ),
                                    }),
                                  )}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4 rounded-lg border p-4">
                        <legend className="px-1 font-semibold">
                          {t("jobs.team")}
                        </legend>
                        <Choice
                          id="proposal-assignee"
                          name="proposal[assignee_id]"
                          label={t("jobs.assignee_id")}
                          options={staff}
                          value={values.assignee_id}
                          onValueChange={(value) =>
                            set({ assignee_id: value || "" })
                          }
                        />
                        <details>
                          <summary className="cursor-pointer py-2">
                            {t("jobs.helpers")}
                          </summary>
                          <div className="mt-3 space-y-3">
                            {staff.map((person) => (
                              <Label
                                key={person.id}
                                className="flex items-center gap-3"
                              >
                                <Checkbox
                                  checked={values.helper_ids.includes(
                                    person.id,
                                  )}
                                  onCheckedChange={(checked) =>
                                    set({
                                      helper_ids: checked
                                        ? [...values.helper_ids, person.id]
                                        : values.helper_ids.filter(
                                            (id) => id !== person.id,
                                          ),
                                    })
                                  }
                                />
                                {person.name}
                              </Label>
                            ))}
                          </div>
                        </details>
                        <p className="text-sm text-muted-foreground">
                          {t("jobs.team_help")}
                        </p>
                      </fieldset>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    type="submit"
                    disabled={
                      form.processing ||
                      (section === "schedule" &&
                        (!bays.length ||
                          !staff.length ||
                          stale ||
                          !serviceCurrency(values.services) ||
                          changedServices(values.services, options)))
                    }
                  >
                    {t(
                      section === "services"
                        ? "product_ux_proposal.continue_schedule"
                        : "proposals.save_preview",
                    )}
                  </Button>
                  <Link
                    className={buttonVariants({ variant: "outline" })}
                    href={locationRequestPath(location, enquiry.id)}
                    onClick={(event) => {
                      if (
                        form.isDirty &&
                        !window.confirm(t("product_ux_proposal.discard_prompt"))
                      ) {
                        event.preventDefault();
                        return;
                      }
                      saved.current = true;
                      forgetProposalDraft(draftKey);
                    }}
                  >
                    {t("common.cancel")}
                  </Link>
                </div>
              </div>
            </form>
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
